import { faPauseCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ImisIntegration } from '@feathr/blackbox';
import { Button, ConfirmModalV1, toast } from '@feathr/components';
import { useToggle } from '@feathr/hooks';

// eslint-disable-next-line @typescript-eslint/naming-convention
interface IiMISPauseControlProps {
  integration: ImisIntegration;
}

function ImisPauseControl({ integration }: IiMISPauseControlProps): JSX.Element | null {
  const { t } = useTranslation();
  const [showPauseModal, togglePauseModal] = useToggle(false);

  function handlePauseModal(): void {
    togglePauseModal();
  }

  function handleCancel(): void {
    if (showPauseModal) {
      togglePauseModal();
    }
  }

  async function handlePause(): Promise<void> {
    try {
      await integration.pauseIntegration();
      toast(t('Your iMIS integration has been successfully paused'), {
        type: 'success',
      });
    } catch (error) {
      toast(
        t('Something went wrong while trying to pause your iMIS integration: {{error}}', {
          error,
        }),
        {
          type: 'error',
        },
      );
    } finally {
      togglePauseModal();
      // TODO: Hard reloading will not be necesary with the implemention #2651.
      window.location.reload();
    }
  }

  return (
    <>
      <Button
        key={'pause-imis-integration'}
        name={'pause-imis-integration'}
        onClick={handlePauseModal}
        prefix={<FontAwesomeIcon icon={faPauseCircle} />}
        type={'primary'}
      >
        {t('Pause integration')}
      </Button>
      {showPauseModal && (
        <ConfirmModalV1
          cancelButtonText={t('Cancel')}
          confirmButtonText={t('Pause')}
          message={t('Are you sure you want to pause your iMIS integration?')}
          onClose={handleCancel}
          onConfirm={handlePause}
          t={t}
          title={t('Pause your iMIS integration?')}
          type={'warning'}
        >
          <p>{t('Data will not sync between Feathr and iMIS when your integration is paused.')}</p>
        </ConfirmModalV1>
      )}
    </>
  );
}

export default observer(ImisPauseControl);
